import { ChevronDownIcon, ExclamationCircleIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import type { ExamItemAnswerAiCorrectionViewModel } from '@/shared/api/generated/types';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Skeleton,
} from '@/shared/ui/atoms';
import { formatGrade, normalizeAiGrade } from '../../../../utils';
import type { AiCorrectionStatus } from '../useCorrectionModal';
import { AiResultDetails } from './AiResultDetails';

/** Valor único do item de accordion que envolve o resultado da análise por IA */
const AI_RESULTS_ITEM = 'ai-results';

export interface AiCorrectionPanelProps {
  /** Estado atual da análise por IA */
  readonly status: AiCorrectionStatus;
  /** Resultado devolvido pela IA, quando houver */
  readonly result: ExamItemAnswerAiCorrectionViewModel | null;
  /** Habilita o disparo da análise (depende de haver uma resposta carregada) */
  readonly canAnalyze: boolean;
  /** Dispara (ou repete) a análise por IA */
  readonly onAnalyze: () => void;
}

/**
 * Painel de correção assistida por IA: disparo da análise, estados de carregamento
 * e erro, e o resultado detalhado dentro de um accordion.
 *
 * A ação de aplicar a sugestão é um botão do rodapé do modal, para não depender do
 * scroll do corpo; a conclusão da análise e a aplicação são anunciadas por toast.
 */
export function AiCorrectionPanel({
  status,
  result,
  canAnalyze,
  onAnalyze,
}: AiCorrectionPanelProps) {
  const { t } = useTranslation('examCorrection');

  const isLoading = status === 'loading';

  return (
    <div className="flex flex-col gap-mini">
      {/* Botão de ação */}
      <div className="flex items-center justify-between gap-mini rounded-md border-hairline border-ctx-layout-border bg-gradient-to-b from-ctx-layout-surface to-ctx-layout-body p-mini">
        <div className="flex flex-col gap-quark">
          <strong className="text-ctx-content-title ts-subtitle-xxs">{t('modal.ai.title')}</strong>
          <span className="text-ctx-content-placeholder ts-caption-xxs">
            {t('modal.ai.subtitle')}
          </span>
        </div>
        <button
          type="button"
          onClick={onAnalyze}
          disabled={isLoading || !canAnalyze}
          className="inline-flex shrink-0 items-center gap-nano whitespace-nowrap rounded-sm border border-ctx-highlight-promote bg-ctx-highlight-promote px-mini py-[10px] text-base-neutral-white ts-subtitle-xxs transition-all hover:border-base-accent-400 hover:bg-base-accent-400 active:border-base-accent-600 active:bg-base-accent-600 disabled:cursor-not-allowed disabled:opacity-70"
        >
          {isLoading ? (
            <span className="size-xxs animate-spin rounded-circular border-thin border-base-neutral-white/intense border-t-base-neutral-white" />
          ) : (
            <SparklesIcon className="size-xxs" />
          )}
          <span>
            {isLoading
              ? t('modal.ai.buttonAnalyzing')
              : result
                ? t('modal.ai.buttonReanalyze')
                : t('modal.ai.button')}
          </span>
        </button>
      </div>

      {/* Skeleton de carregamento */}
      {isLoading && (
        <div aria-hidden="true" className="flex flex-col gap-micro">
          <Skeleton className="h-[6rem] w-full rounded-md" />
          <div className="grid grid-cols-2 gap-micro sm:grid-cols-4">
            <Skeleton className="h-[110px]" />
            <Skeleton className="h-[110px]" />
            <Skeleton className="h-[110px]" />
            <Skeleton className="h-[110px]" />
          </div>
          <Skeleton className="h-[140px]" />
        </div>
      )}

      {/* Banner de erro */}
      {status === 'error' && (
        <div
          role="alert"
          className="flex items-start gap-micro rounded-sm border-hairline border-base-danger-300 bg-base-danger-100 p-micro text-base-danger-800 ts-paragraph-xxs"
        >
          <ExclamationCircleIcon className="mt-quark size-xxs shrink-0" />
          <div>
            <p className="font-semibold">{t('modal.ai.errorTitle')}</p>
            <span>{t('modal.ai.errorMessage')} </span>
            <button type="button" onClick={onAnalyze} className="font-medium underline">
              {t('modal.ai.retry')}
            </button>
          </div>
        </div>
      )}

      {/* Resultados da IA */}
      {status === 'success' && result && (
        <div aria-live="polite" className="flex flex-col gap-mini">
          {/* Resultado retrátil */}
          <Accordion type="single" collapsible defaultValue={AI_RESULTS_ITEM}>
            <AccordionItem value={AI_RESULTS_ITEM}>
              {/* O texto visível é o nome acessível do gatilho; o estado
                  aberto/fechado vem do aria-expanded do Radix */}
              <AccordionTrigger className="justify-between gap-nano rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface px-xxs py-mini hover:bg-ctx-layout-body">
                <span className="flex items-center gap-nano text-ctx-content-title ts-subtitle-xxs">
                  <SparklesIcon className="size-xxs text-ctx-content-base" />
                  {t('modal.ai.results.title')}
                </span>
                <span className="flex items-center gap-nano">
                  {result.suggestedGrade !== undefined && (
                    <span className="text-ctx-content-placeholder tabular-nums ts-caption-xxs">
                      {`${t('modal.ai.results.suggestedGrade')}: ${formatGrade(normalizeAiGrade(result.suggestedGrade))}/100`}
                    </span>
                  )}
                  <ChevronDownIcon className="size-xxs shrink-0 text-ctx-content-placeholder transition-transform group-data-[state=open]:rotate-180" />
                </span>
              </AccordionTrigger>

              <AccordionContent>
                <AiResultDetails result={result} />
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      )}
    </div>
  );
}
